import {
	print,
	type DocumentNode,
	type OperationDefinitionNode,
} from 'graphql';
import { ClientError } from 'graphql-request';

import { report } from './observability';
import { getObservabilityTokenClaims } from './token-claims';

/**
 * Operations whose variables carry a plaintext secret -- never captured,
 * regardless of the whitelist below. `AuthenticateAdmin`
 * (`src/shared/api/documents/drafts.ts`) takes a plaintext admin password
 * as a variable; capturing it would write a plaintext secret into the
 * local store and, later, into whatever remote reporter replaces it.
 */
const EXCLUDED_OPERATIONS = new Set(['AuthenticateAdmin']);

function resolveDocumentText(document: string | DocumentNode): string {
	return typeof document === 'string' ? document : print(document);
}

function resolveOperationName(
	document: string | DocumentNode,
	documentText: string,
): string {
	if (typeof document !== 'string') {
		const operationDef = document.definitions.find(
			(definition): definition is OperationDefinitionNode =>
				definition.kind === 'OperationDefinition',
		);
		if (operationDef?.name?.value) {
			return operationDef.name.value;
		}
	}
	// Fallback for the plain-string call sites codegen hasn't migrated yet
	// (graphqlFetch's string overload) -- same shape codegen documents use.
	const match = /\b(?:query|mutation|subscription)\s+(\w+)/.exec(
		documentText,
	);
	return match?.[1] ?? 'anonymous';
}

/**
 * The single `query_error` call site, used by `graphqlFetch`'s error path
 * only -- i.e. genuine remote-request failures, never local/cache errors
 * (those have nothing to do with a token or a GraphQL document).
 */
export function reportQueryError(params: {
	document: string | DocumentNode;
	variables?: Record<string, unknown>;
	error: unknown;
}): void {
	const documentText = resolveDocumentText(params.document);
	const operation = resolveOperationName(params.document, documentText);

	if (EXCLUDED_OPERATIONS.has(operation)) {
		return;
	}

	const statusCode =
		params.error instanceof ClientError
			? params.error.response.status
			: undefined;

	report({
		name: 'query_error',
		payload: {
			operation,
			statusCode,
			document: documentText,
			variables: params.variables,
			tokenClaims: getObservabilityTokenClaims(),
		},
	});
}
